import React, { useState, useEffect, useCallback } from "react";
import {
  FaSpinner,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaBalanceScale,
  FaSearch,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

export default function TrialBalance() {
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const currentYear = today.getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);

  const [endDate, setEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [tbRows, setTbRows] = useState([]);
  const [totalDebits, setTotalDebits] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  const [isBalanced, setIsBalanced] = useState(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `ETB ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", `${startDate}T00:00:00`);
      if (endDate) params.append("endDate", `${endDate}T23:59:59`);

      const res = await apiRequest(
        `reports/trial-balance?${params.toString()}`
      );
      const payload = res?.data || res;

      setTbRows(payload?.rows || []);
      setTotalDebits(payload?.totalDebits || 0);
      setTotalCredits(payload?.totalCredits || 0);
      setIsBalanced(
        payload?.isBalanced !== undefined ? payload.isBalanced : true
      );
    } catch (err) {
      setError(
        err?.message || "Failed to load trial balance. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const filteredRows = tbRows.filter((row) => {
    const targetName = (row.name || row.accountName || "").toLowerCase();
    const targetType = (row.type || row.accountType || "").toLowerCase();
    const search = searchTerm.toLowerCase();
    return targetName.includes(search) || targetType.includes(search);
  });

  const inputCls =
    "w-full rounded-xl pl-10 pr-4 py-2 text-sm transition focus:outline-none focus:border-emerald-500/50 bg-white border border-slate-200 text-slate-800 dark:bg-slate-900/50 dark:border-white/[0.08] dark:text-slate-200";

  const card =
    "p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-lg";

  return (
    <div className="min-h-full relative overflow-hidden font-sans p-3 md:p-6 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      {/* Background glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/5 blur-[120px] dark:bg-blue-500/10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/5 blur-[150px] dark:bg-emerald-500/10" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-6">
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 rounded-2xl gap-4 border shadow-sm backdrop-blur-xl bg-white border-slate-200 dark:bg-white/[0.03] dark:border-white/[0.08] dark:shadow-2xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:bg-gradient-to-r dark:from-white dark:via-slate-200 dark:to-slate-400 dark:bg-clip-text dark:text-transparent flex items-center gap-3">
              <FaBalanceScale className="text-blue-600 dark:text-blue-400" />
              Trial Balance
            </h1>
            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
              Ledger balances • Debits vs Credits
            </p>
          </div>
        </div>

        {/* FILTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-4 rounded-xl border backdrop-blur-md bg-white border-slate-200 dark:bg-white/[0.02] dark:border-white/[0.05]">
          <div className="relative flex items-center">
            <FaSearch className="absolute left-3 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Filter accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={inputCls}
            />
          </div>

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              Start Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              End Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={card}>
              <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                Total Debits
              </span>
              <h2 className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                {formatCurrency(totalDebits)}
              </h2>
            </div>
            <div className={card}>
              <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                Total Credits
              </span>
              <h2 className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {formatCurrency(totalCredits)}
              </h2>
            </div>
            <div
              className={`p-5 rounded-2xl border backdrop-blur-xl flex items-center justify-between ${
                isBalanced
                  ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-500/[0.02] dark:border-emerald-500/20"
                  : "bg-rose-50 border-rose-200 dark:bg-rose-500/[0.02] dark:border-rose-500/20"
              }`}
            >
              <div>
                <span className="text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                  Balancing Check
                </span>
                <h2
                  className={`text-xl font-bold mt-1 ${
                    isBalanced
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-rose-600 dark:text-rose-400"
                  }`}
                >
                  {isBalanced ? "Balanced" : "Discrepancy Detected"}
                </h2>
              </div>
              {isBalanced ? (
                <FaCheckCircle className="text-emerald-500 dark:text-emerald-400 text-2xl" />
              ) : (
                <FaExclamationTriangle className="text-rose-500 dark:text-rose-400 text-2xl" />
              )}
            </div>
          </div>
        )}

        {/* LOADING */}
        {loading && (
          <div className="text-center py-12 rounded-2xl border backdrop-blur-md text-slate-500 bg-white border-slate-200 dark:text-slate-400 dark:bg-white/[0.01] dark:border-white/[0.05]">
            <FaSpinner className="animate-spin inline mr-3 text-blue-500 text-lg" />
            Loading trial balance...
          </div>
        )}

        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-3 justify-center text-center py-8 rounded-2xl border text-rose-600 bg-rose-50 border-rose-200 dark:text-rose-400 dark:bg-rose-950/20 dark:border-rose-500/20">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {/* TABLE */}
        {!loading && !error && (
          <div
            className="rounded-2xl overflow-hidden shadow-sm backdrop-blur-2xl border
            bg-white border-slate-200
            dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-2xl"
          >
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr
                    className="text-xs font-semibold uppercase tracking-wider border-b
                    bg-slate-50 border-slate-200 text-slate-500
                    dark:bg-white/[0.04] dark:border-white/[0.08] dark:text-slate-300"
                  >
                    <th className="py-4 px-6">Account Name</th>
                    <th className="py-4 px-6 w-32">Type</th>
                    <th className="py-4 px-6 text-right w-40">Debit</th>
                    <th className="py-4 px-6 text-right w-40">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y text-slate-600 divide-slate-100 dark:divide-white/[0.04] dark:text-slate-300">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="py-12 text-center italic text-slate-500"
                      >
                        No ledger rows match the current filter criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr
                        key={row.id || row.accountId || row.name}
                        className="transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.01]"
                      >
                        <td className="py-4 px-6 font-bold text-slate-800 dark:text-white">
                          {row.name || row.accountName}
                        </td>
                        <td className="py-4 px-6">
                          <span
                            className="text-xs font-mono px-2.5 py-1 rounded border
                            border-slate-200 bg-slate-50 text-slate-600
                            dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
                          >
                            {row.type || row.accountType}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right font-mono text-blue-600 dark:text-blue-400">
                          {row.totalDebit > 0
                            ? formatCurrency(row.totalDebit)
                            : "—"}
                        </td>
                        <td className="py-4 px-6 text-right font-mono text-purple-600 dark:text-purple-400">
                          {row.totalCredit > 0
                            ? formatCurrency(row.totalCredit)
                            : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3 p-4">
              {filteredRows.length === 0 ? (
                <div className="text-center py-12 italic text-slate-500">
                  No ledger rows match the current filter criteria.
                </div>
              ) : (
                filteredRows.map((row) => (
                  <div
                    key={row.id || row.accountId || row.name}
                    className="rounded-xl p-4 space-y-3 border
                      bg-slate-50 border-slate-200
                      dark:bg-white/[0.03] dark:border-white/[0.08]"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold text-[15px] leading-tight mt-0.5 text-slate-800 dark:text-white">
                          {row.name || row.accountName}
                        </div>
                      </div>
                      <span
                        className="text-xs font-mono px-2.5 py-1 rounded border
                        border-slate-200 bg-white text-slate-500
                        dark:border-white/10 dark:bg-white/5 dark:text-slate-400"
                      >
                        {row.type || row.accountType}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-sm pt-2 border-t border-slate-200 dark:border-white/[0.06]">
                      <div>
                        <div className="text-xs text-slate-500">Debit</div>
                        <div className="font-mono mt-0.5 text-blue-600 dark:text-blue-400">
                          {row.totalDebit > 0
                            ? formatCurrency(row.totalDebit)
                            : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Credit</div>
                        <div className="font-mono mt-0.5 text-purple-600 dark:text-purple-400">
                          {row.totalCredit > 0
                            ? formatCurrency(row.totalCredit)
                            : "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">
                          Net Balance
                        </div>
                        <div className="font-mono font-semibold mt-0.5 text-slate-800 dark:text-slate-100">
                          {formatCurrency(row.netBalance)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
